import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  realtimeEventsSubscribe,
  realtimeEventsUnsubscribe,
} from 'actions/RealtimeEventActions';

/**
 * Subscribe to a realtime event type and optional entity rooms.
 *
 * @param {string} eventType
 * @param {(event: object) => void} handler
 * @param {{ subscribeRooms?: string[], enabled?: boolean }} options
 */
export default function useRealtimeEvent(eventType, handler, options = {}) {
  const dispatch = useDispatch();
  const { subscribeRooms = [], enabled = true } = options;
  const recentEvents = useSelector(state => state.realtimeEvents?.recentEvents ?? []);
  const isConnected = useSelector(state => state.realtimeEvents?.isConnected ?? false);
  const handlerRef = useRef(handler);
  const roomsKey = subscribeRooms.join('|');

  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled || !isConnected || subscribeRooms.length === 0) {
      return undefined;
    }

    dispatch(realtimeEventsSubscribe(subscribeRooms));

    return () => {
      dispatch(realtimeEventsUnsubscribe(subscribeRooms));
    };
  }, [dispatch, enabled, isConnected, roomsKey]);

  const lastEventIdRef = useRef(null);

  useEffect(() => {
    if (!enabled || !eventType) {
      return;
    }

    const latest = recentEvents.find(e => e?.type === eventType);
    if (latest && latest.id !== lastEventIdRef.current) {
      lastEventIdRef.current = latest.id;
      handlerRef.current(latest);
    }
  }, [recentEvents, eventType, enabled]);
}
